import React from 'react';
import { AlertTriangle, Copy, Moon, PauseCircle, Repeat, UserX } from 'lucide-react';
import { DISCIPLINE_BORDER, STATUS_STYLES, Shift } from '../../dtos/scheduling/schedule.dto';
import { formatMinutes, hhaxCopyText, shiftWindow } from './scheduleUtils';
import { useToast } from '../common/Toast';

export interface ShiftCardProps {
  shift: Shift;
  /** 'full' for lists and the Today board; 'compact' for calendar cells */
  variant?: 'full' | 'compact';
  /** What the card leads with: the patient (coordinator views) or the clinician (patient tab) */
  primary?: 'patient' | 'provider';
  selected?: boolean;
  onSelect?: (shift: Shift, additive: boolean) => void;
  onOpen?: (shift: Shift) => void;
  /** This card is the tail of an overnight shift that started the day before */
  carryOver?: boolean;
  actions?: React.ReactNode;
}

const HELD = ['on_hold'];

/**
 * One shift. Discipline is the left border colour; status is a pill; held patients are amber
 * (HLD-03); open shifts have a dashed border; Late shows a red marker (plan §6.1).
 */
const ShiftCard: React.FC<ShiftCardProps> = ({
  shift, variant = 'full', primary = 'patient', selected = false, onSelect, onOpen, carryOver = false, actions,
}) => {
  const { showToast } = useToast();
  const held = HELD.includes(String(shift.patientStatus));
  const unstaffed = !shift.providerId && shift.status === 'SCHEDULED';
  const late = shift.flags?.late || shift.flags?.missed;
  const cancelled = shift.status === 'CANCELLED' || shift.status === 'NO_SHOW';
  const border = DISCIPLINE_BORDER[String(shift.discipline).toUpperCase()] || 'border-l-slate-400';
  const status = STATUS_STYLES[shift.status] || STATUS_STYLES.SCHEDULED;
  const primaryText = primary === 'patient' ? shift.patient?.name || 'Patient' : shift.provider?.name || 'Unstaffed';
  const secondaryText = primary === 'patient' ? (shift.provider?.name || 'Unstaffed') : shift.patient?.name;

  const copyRef = async (event: React.MouseEvent) => {
    event.stopPropagation();
    try {
      await navigator.clipboard.writeText(hhaxCopyText(shift));
      showToast(`Copied ${shift.scheduleRef} for HHAX`, { variant: 'success', durationMs: 2500 });
    } catch {
      showToast('Copy failed; select the reference and copy it manually', { variant: 'error' });
    }
  };

  const classes = [
    'group relative w-full rounded-md border border-l-4 text-left transition-shadow',
    border,
    held ? 'bg-amber-50 border-amber-300' : cancelled ? 'bg-gray-50 border-gray-200' : 'bg-white border-gray-200',
    unstaffed ? 'border-dashed' : '',
    selected ? 'ring-2 ring-slate-700' : 'hover:shadow-sm',
    variant === 'compact' ? 'px-1.5 py-1' : 'px-3 py-2',
  ].join(' ');

  const body = variant === 'compact' ? (
    <div className={`min-w-0 text-xs ${cancelled ? 'text-gray-400 line-through' : 'text-gray-800'}`}>
      <div className="flex items-center gap-1 font-medium">
        {late && <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-red-500" aria-label="Late" />}
        <span className="truncate">{shiftWindow(shift, true)}</span>
      </div>
      <div className="truncate">{primaryText}</div>
      {unstaffed && <div className="truncate text-red-600">Unstaffed</div>}
    </div>
  ) : (
    <div className="min-w-0">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className={`truncate text-sm font-semibold ${cancelled ? 'text-gray-400 line-through' : 'text-gray-900'}`}>{primaryText}</div>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-sm text-gray-600">
            <span>{shiftWindow(shift)}</span>
            {shift.durationMinutes ? <span className="text-gray-400">· {formatMinutes(shift.durationMinutes)}</span> : null}
            {shift.overnight && <Moon size={13} className="text-gray-400" aria-label="Overnight" />}
            {shift.isRecurring && <Repeat size={13} className="text-gray-400" aria-label="Repeats" />}
          </div>
        </div>
        <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${status.pill}`}>{status.label}</span>
      </div>
      <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-600">
        <span className={unstaffed ? 'flex items-center gap-1 font-medium text-red-600' : ''}>
          {unstaffed && <UserX size={12} />}{secondaryText} · {shift.discipline} {shift.visitType}
        </span>
        {held && <span className="flex items-center gap-1 font-medium text-amber-700"><PauseCircle size={12} />On hold</span>}
        {late && <span className="flex items-center gap-1 font-medium text-red-600"><AlertTriangle size={12} />{shift.flags.missed ? 'Missed' : 'Late'}</span>}
        {carryOver && <span className="text-gray-400">from previous night</span>}
        {shift.cancel && <span className="text-gray-500">Cancelled: {shift.cancel.code.toLowerCase()}</span>}
        {shift.scheduleRef && (
          <button
            type="button"
            onClick={copyRef}
            className="ml-auto flex min-h-[32px] items-center gap-1 rounded px-1.5 font-mono text-gray-500 hover:bg-gray-100 hover:text-gray-800"
            title="Copy the schedule reference, clinician code, date and times for HHAX"
          >
            <Copy size={12} />{shift.scheduleRef}
          </button>
        )}
      </div>
      {actions && <div className="mt-2 flex flex-wrap gap-2">{actions}</div>}
    </div>
  );

  if (!onOpen && !onSelect) return <div className={classes}>{body}</div>;
  return (
    <div
      role="button"
      tabIndex={0}
      aria-pressed={onSelect ? selected : undefined}
      className={`${classes} cursor-pointer`}
      onClick={(event) => {
        if (onSelect && (event.shiftKey || event.metaKey || event.ctrlKey)) onSelect(shift, true);
        else onOpen?.(shift);
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onOpen?.(shift);
        }
      }}
    >
      {body}
    </div>
  );
};

export default ShiftCard;
